import React, { useEffect, useMemo, useState } from "react";
import Grid2 from "@mui/material/Unstable_Grid2";
import { useAppDispatch, useAppSelector } from "../../framework/customStore";
import {
    clearReportFilters as clearFilters,
    setReportSelectedCostCenters as setSelectedCostCenters,
    setReportSelectedCustomers as setSelectedCustomers,
    setReportSelectedEmployeeGroups as setSelectedEmployeeGroups,
    setReportSelectedEmployees as setSelectedEmployees,
    setReportSelectedGeofences as setSelectedGeofences,
    setReportSelectedVehicles as setSelectedVehicles,
    setReportSelectedWorkTimeTypes as setSelectedWorkTimeTypes,
    setReportTimeRange as setTimeRange,
} from "../../store/reportTemplateSlice";
import { EmployeeListItemDto } from "../../models/employee/employee";
import { useTranslation } from "react-i18next";
import { MuiDateRangePicker } from "../framework/muiDateRangePicker";
import {
    DateRangeShortcutButton,
    ShortcutOption,
} from "../framework/dateRangeShortcuts";
import {
    AutocompleteOption,
    AutocompleteValue,
    AutocompleteWrapper,
} from "../framework/muiAutocomplete";
import { AnyAction } from "@reduxjs/toolkit";
import { employeeGroupListItemDto } from "../../models/employeeGroup/employeeGroup";
import { EmployeeGroupCalculation } from "../../hooks/reporting/useWorkTimeReportData";
import { WorkTimeReportCalculationDialog } from "./WorkTimeReportCalculationDialog";
import { SalaryPeriodTypeDto } from "../../models/salaryPeriodType/salaryPeriodType";
import { ReportButton } from "./ReportSection";
import Button from "@mui/material/Button";
import { SalaryPeriodDto } from "../../models/salaryPeriod/salaryPeriod";
import { useReportOptionOptions } from "../../hooks/reporting/useReportOptions";

export const WorkTimeReportFilters = ({
    employees,
    employeeGroups,
    salaryPeriodTypes,
    employeeGroupCalculations,
    reload,
    geofencesEnabled,
}: {
    employees: EmployeeListItemDto[];
    employeeGroups: employeeGroupListItemDto[];
    salaryPeriodTypes: SalaryPeriodTypeDto[];
    employeeGroupCalculations: EmployeeGroupCalculation[];
    reload: () => void;
    geofencesEnabled: boolean;
}) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const [selectedSalaryPeriodTypeId, setSelectedSalaryPeriodTypeId] =
        useState<string | null>(null);
    const [salaryPeriodNextDisabled, setSalaryPeriodNextDisabled] =
        useState(true);
    const [salaryPeriodPreviousDisabled, setSalaryPeriodPreviousDisabled] =
        useState(true);
    const [previousSalaryPeriod, setPreviousSalaryPeriod] =
        useState<SalaryPeriodDto | null>(null);
    const [nextSalaryPeriod, setNextSalaryPeriod] =
        useState<SalaryPeriodDto | null>(null);
    const salaryPeriods = useMemo(
        () =>
            salaryPeriodTypes?.find((t) => t.id === selectedSalaryPeriodTypeId)
                ?.salaryPeriods,
        [salaryPeriodTypes, selectedSalaryPeriodTypeId]
    );

    const timeRange = useAppSelector(
        (state) => state.reportTemplate.current.timeRange
    );
    const selectedVehicles = useAppSelector(
        (state) => state.reportTemplate.current.filters.selectedVehicles
    );
    const selectedCostCenters = useAppSelector(
        (state) => state.reportTemplate.current.filters.selectedCostCenters
    );
    const selectedEmployees = useAppSelector(
        (state) => state.reportTemplate.current.filters.selectedEmployees
    );
    const selectedEmployeeGroups = useAppSelector(
        (state) => state.reportTemplate.current.filters.selectedEmployeeGroups
    );
    const selectedCustomers = useAppSelector(
        (state) => state.reportTemplate.current.filters.selectedCustomers
    );
    const selectedWorkTimeTypes = useAppSelector(
        (state) => state.reportTemplate.current.filters.selectedWorkTimeTypes
    );
    const selectedGeofences = useAppSelector(
        (state) => state.reportTemplate.current.filters.selectedGeofences
    );

    const geofences = useAppSelector((state) => state.reportOptions.geofences);

    const costCenterOptionsWithGroups = useAppSelector(
        (state) => state.reportOptions.costCenterOptions
    );

    useEffect(() => {
        if (employeeGroups?.length > 0 && selectedEmployeeGroups?.length > 0) {
            const employeeGroupsById = employeeGroups.filter((employeeGroup) =>
                selectedEmployeeGroups.includes(employeeGroup.id)
            );
            // Get unique salaryPeriodTypeIds from selected employeeGroups
            // and then check that there are no multiple IDs mixed.
            // Allow mixing employee groups where salaryPeriodTypeId is not set.
            const salaryPeriodTypeIds = [
                ...new Set(
                    employeeGroupsById.map((item) => item.salaryPeriodTypeId)
                ),
            ].filter((x) => x !== null);

            if (salaryPeriodTypeIds?.length === 1) {
                setSelectedSalaryPeriodTypeId(salaryPeriodTypeIds[0]);
            } else {
                disableSalaryPeriodShortcut();
            }
        } else {
            disableSalaryPeriodShortcut();
        }
    }, [employeeGroups, selectedEmployeeGroups]);

    useEffect(() => {
        if (selectedSalaryPeriodTypeId && salaryPeriods?.length > 0) {
            let currentSalaryPeriod: SalaryPeriodDto = null;
            const pastSalaryPeriods = salaryPeriods.filter(
                (sp) =>
                    sp.salaryPeriodTypeId === selectedSalaryPeriodTypeId &&
                    sp.startDate <= timeRange[1]
            );
            const futureSalaryPeriods = salaryPeriods.filter(
                (sp) =>
                    sp.salaryPeriodTypeId === selectedSalaryPeriodTypeId &&
                    sp.startDate >= timeRange[1]
            );

            if (pastSalaryPeriods?.length > 1) {
                currentSalaryPeriod =
                    pastSalaryPeriods[pastSalaryPeriods.length - 1];
            } else {
                if (pastSalaryPeriods?.length === 1) {
                    currentSalaryPeriod = pastSalaryPeriods[0];
                }
            }

            if (currentSalaryPeriod) {
                const currentSalaryPeriodIndex =
                    salaryPeriods.indexOf(currentSalaryPeriod);
                const lastSalaryPeriodIndex = salaryPeriods.length - 1;

                if (currentSalaryPeriodIndex > 0) {
                    setPreviousSalaryPeriod(
                        salaryPeriods[currentSalaryPeriodIndex - 1]
                    );
                    setSalaryPeriodPreviousDisabled(false);
                } else {
                    setSalaryPeriodPreviousDisabled(true);
                }

                if (currentSalaryPeriodIndex < lastSalaryPeriodIndex) {
                    setNextSalaryPeriod(
                        salaryPeriods[currentSalaryPeriodIndex + 1]
                    );
                    setSalaryPeriodNextDisabled(false);
                } else {
                    setSalaryPeriodNextDisabled(true);
                }
            } else if (futureSalaryPeriods?.length > 0) {
                setNextSalaryPeriod(futureSalaryPeriods[0]);
                setSalaryPeriodNextDisabled(false);
                setSalaryPeriodPreviousDisabled(true);
            }
        }
    }, [selectedSalaryPeriodTypeId, salaryPeriods, timeRange]);

    const disableSalaryPeriodShortcut = () => {
        setSalaryPeriodPreviousDisabled(true);
        setSalaryPeriodNextDisabled(true);
        setSelectedSalaryPeriodTypeId(null);
        setPreviousSalaryPeriod(null);
        setNextSalaryPeriod(null);
    };

    const setTimeRangeBySalaryPeriod = (salaryPeriod: SalaryPeriodDto) => {
        if (salaryPeriod) {
            dispatch(
                setTimeRange([salaryPeriod.startDate, salaryPeriod.endDate])
            );
        }
    };

    const [vehicleOptions, selectedVehicleNames, getVehicleIdsFromOptions] =
        useReportOptionOptions(
            "vehicle",
            selectedVehicles,
            "id",
            "registerNumber"
        );

    const [
        workTimeTypeOptions,
        selectedWorkTimeTypeNames,
        getWorkTimeTypeIdsFromOptions,
    ] = useReportOptionOptions(
        "workTimeType",
        selectedWorkTimeTypes,
        "workTimeTypeId",
        "name"
    );

    const [customerOptions, selectedCustomerNames, getCustomerIdsFromOptions] =
        useReportOptionOptions("customer", selectedCustomers, "id", "name");

    const geofenceOptions: AutocompleteOption[] = useMemo(() => {
        if (!geofences) return [];
        return geofences
            .filter((g) => {
                if (
                    selectedCustomers?.length > 0 &&
                    g.customerId &&
                    !selectedCustomers.includes(g.customerId)
                ) {
                    return false;
                }
                if (
                    selectedCostCenters?.length > 0 &&
                    g.costCenterId &&
                    !selectedCostCenters.includes(g.costCenterId)
                ) {
                    return false;
                }
                return true;
            })
            .map((g) => ({
                id: g.geofenceId,
                title: g.name,
                group: g.customerName || "",
            }));
    }, [geofences, selectedCustomers, selectedCostCenters]);

    return (
        <Grid2 container spacing={2}>
            <Grid2 container xs={12}>
                <Grid2 xs="auto">
                    <MuiDateRangePicker
                        size="small"
                        label={t("reporting.dateRange")}
                        value={timeRange}
                        onChange={(val) => dispatch(setTimeRange(val))}
                    />
                </Grid2>
                <Grid2>
                    <DateRangeShortcutButton
                        size="large"
                        option={ShortcutOption.PreviousWeek}
                        dateRange={timeRange}
                        setDateRange={(val) => dispatch(setTimeRange(val))}
                    />
                </Grid2>
                <Grid2>
                    <DateRangeShortcutButton
                        size="large"
                        option={ShortcutOption.NextWeek}
                        dateRange={timeRange}
                        setDateRange={(val) => dispatch(setTimeRange(val))}
                    />
                </Grid2>
                <Grid2>
                    <DateRangeShortcutButton
                        size="large"
                        option={ShortcutOption.PreviousMonth}
                        dateRange={timeRange}
                        setDateRange={(val) => dispatch(setTimeRange(val))}
                    />
                </Grid2>
                <Grid2>
                    <DateRangeShortcutButton
                        size="large"
                        option={ShortcutOption.NextMonth}
                        dateRange={timeRange}
                        setDateRange={(val) => dispatch(setTimeRange(val))}
                    />
                </Grid2>
                <Grid2>
                    <Button
                        size="large"
                        variant="outlined"
                        onClick={() =>
                            setTimeRangeBySalaryPeriod(previousSalaryPeriod)
                        }
                        disabled={salaryPeriodPreviousDisabled}
                    >
                        {t("common.datePickerShortCut.previousSalaryPeriod")}
                    </Button>
                </Grid2>
                <Grid2>
                    <Button
                        size="large"
                        variant="outlined"
                        onClick={() =>
                            setTimeRangeBySalaryPeriod(nextSalaryPeriod)
                        }
                        disabled={salaryPeriodNextDisabled}
                    >
                        {t("common.datePickerShortCut.nextSalaryPeriod")}
                    </Button>
                </Grid2>
                <Grid2
                    xsOffset="auto"
                    alignItems="self-end"
                    alignContent="center"
                >
                    <WorkTimeReportCalculationDialog
                        employeeGroupCalculations={employeeGroupCalculations}
                        reload={reload}
                    />
                </Grid2>
            </Grid2>
            <Grid2 container xs={12} spacing={2}>
                <FilterColumn>
                    <AutocompleteFilter
                        label={t("reporting.employeeGroups")}
                        value={selectedEmployeeGroups}
                        setValue={setSelectedEmployeeGroups}
                        options={employeeGroups.map((g) => ({
                            id: g.id,
                            title: g.name,
                        }))}
                    />
                </FilterColumn>
                <FilterColumn>
                    <AutocompleteFilter
                        label={t("reporting.employees")}
                        value={selectedEmployees}
                        setValue={setSelectedEmployees}
                        options={(employees || [])
                            .filter(
                                (e) =>
                                    !selectedEmployeeGroups ||
                                    selectedEmployeeGroups.length === 0 ||
                                    selectedEmployeeGroups.includes(
                                        e.salaryGroupId
                                    )
                            )
                            .map((g) => ({
                                id: g.id,
                                title: g.fullName,
                            }))}
                    />
                </FilterColumn>
                <FilterColumn>
                    <AutocompleteFilter
                        label={t("reporting.fleet")}
                        value={selectedVehicleNames}
                        setValue={setSelectedVehicles}
                        options={vehicleOptions}
                        getIdsFromOptions={getVehicleIdsFromOptions}
                    />
                </FilterColumn>
                <FilterColumn>
                    <AutocompleteFilter
                        label={t("reporting.customers")}
                        value={selectedCustomerNames}
                        setValue={setSelectedCustomers}
                        options={customerOptions}
                        getIdsFromOptions={getCustomerIdsFromOptions}
                    />
                </FilterColumn>
                <FilterColumn>
                    <AutocompleteFilter
                        label={t("reporting.costCenters")}
                        value={selectedCostCenters}
                        setValue={setSelectedCostCenters}
                        options={costCenterOptionsWithGroups}
                        groupBy={(option) => option.group}
                    />
                </FilterColumn>
                <FilterColumn>
                    <AutocompleteFilter
                        label={t("reporting.workTimeTypes")}
                        value={selectedWorkTimeTypeNames}
                        setValue={setSelectedWorkTimeTypes}
                        options={workTimeTypeOptions}
                        getIdsFromOptions={getWorkTimeTypeIdsFromOptions}
                    />
                </FilterColumn>
                {geofencesEnabled && (
                    <FilterColumn>
                        <AutocompleteFilter
                            label={t("reporting.geofenceSelect")}
                            value={selectedGeofences}
                            setValue={setSelectedGeofences}
                            options={geofenceOptions}
                            groupBy={(option) => option.group}
                        />
                    </FilterColumn>
                )}
                <Grid2 xsOffset="auto" alignItems="self-end">
                    <ReportButton
                        onClick={() => {
                            disableSalaryPeriodShortcut();
                            dispatch(clearFilters());
                        }}
                    >
                        {t("common.clear")}
                    </ReportButton>
                </Grid2>
            </Grid2>
        </Grid2>
    );
};

interface AutocompleteFilterProps {
    label: string;
    value: string[] | null;
    options: AutocompleteOption[];
    setValue: (value: string[]) => AnyAction;
    groupBy?: (o: AutocompleteOption) => string;
    getIdsFromOptions?: (options: AutocompleteOption[]) => string[];
}

const AutocompleteFilter = ({
    label,
    value,
    options,
    setValue,
    groupBy,
    getIdsFromOptions,
}: AutocompleteFilterProps) => {
    const dispatch = useAppDispatch();

    return (
        <AutocompleteWrapper
            label={label}
            size="small"
            value={value ?? []}
            multiple={true}
            limitTags={2}
            fullWidth={true}
            options={options}
            groupBy={groupBy}
            onChange={(value: AutocompleteValue | null) =>
                dispatch(
                    setValue(
                        !!getIdsFromOptions
                            ? getIdsFromOptions(value as AutocompleteOption[])
                            : ((value as AutocompleteOption[]) ?? []).map(
                                  (o) => o.id
                              )
                    )
                )
            }
        />
    );
};

const FilterColumn = ({ children }: { children: JSX.Element }) => (
    <Grid2 xs={12} sm={6} md={4} lg={2} xl={true}>
        {children}
    </Grid2>
);
